import React from 'react';
import { Helmet } from 'react-helmet-async';

// SEO Component for page-specific meta tags and structured data
const SEO = ({ 
  title, 
  description, 
  keywords,
  canonicalUrl,
  pageType = 'website',
  image = '/mim-og-image.jpg',
  structuredData = null
}) => {
  const siteTitle = 'Mentors in Motion';
  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} | Youth Development & Creative Learning in St. Louis`;
  const siteUrl = 'https://mimstl.org';
  const defaultDescription = 'Mentors in Motion is a 501(c)(3) nonprofit in St. Louis dedicated to empowering young people through tutoring, mentoring, and creative learning programs including music production, apparel design, and videography.';
  
  const metaDescription = description || defaultDescription;
  const metaImage = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const canonical = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;

  // Base Organization Schema (always included)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "NonprofitOrganization",
    "@id": `${siteUrl}/#organization`,
    "name": "Mentors in Motion",
    "alternateName": ["MiM", "MIM STL"],
    "url": siteUrl,
    "logo": `${siteUrl}/logo512.png`,
    "image": `${siteUrl}/mim-og-image.jpg`,
    "description": defaultDescription,
    "slogan": "Empowering Youth Through Creative Learning",
    "foundingDate": "2009",
    "nonprofitStatus": "501c3",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "St. Louis",
      "addressRegion": "MO",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "admin@mimstl.org",
      "contactType": "customer service"
    },
    "sameAs": [
      "https://www.facebook.com/mimstl314/",
      "https://www.instagram.com/mimstl",
      "https://www.linkedin.com/company/mentors-in-motion-stl"
    ],
    "knowsAbout": [
      "Youth Mentoring",
      "Tutoring",
      "Music Production Education",
      "Apparel Design",
      "Videography",
      "After-School Programs",
      "Creative Learning",
      "Youth Development"
    ],
    "areaServed": {
      "@type": "City",
      "name": "St. Louis",
      "containedInPlace": {
        "@type": "State",
        "name": "Missouri"
      }
    }
  };

  // Local Business Schema for local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Mentors in Motion",
    "image": `${siteUrl}/mim-og-image.jpg`,
    "url": siteUrl,
    "telephone": "",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "",
      "addressLocality": "St. Louis",
      "addressRegion": "MO",
      "postalCode": "",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 38.6270,
      "longitude": -90.1994
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "priceRange": "Free"
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={pageType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="geo.region" content="US-MO" />
      <meta name="geo.placename" content="St. Louis" />
      
      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {/* Structured Data - Local Business */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      {/* Custom Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-built structured data generators
export const createFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://mimstl.org${item.url}`
  }))
});

export const createServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": service.type,
  "name": service.name,
  "description": service.description,
  "provider": {
    "@type": "NonprofitOrganization",
    "name": "Mentors in Motion"
  },
  "areaServed": {
    "@type": "City",
    "name": "St. Louis"
  },
  "audience": {
    "@type": "PeopleAudience",
    "audienceType": "Youth ages 10-18"
  }
});

export default SEO;
